// The catalog editor's pieces for Sell items: where edits stand with Shopify, the bulk-edit bar,
// and parsing for edited cells.
import { CircleAlert, CircleCheck, LoaderCircle, Pencil, RotateCcw, X } from "lucide-react";
import { type FormEvent, useMemo, useState } from "react";
import { calc, numberOf } from "../../../shared/calc.ts";
import type { Item, ShopifyProduct } from "../../../shared/db.gen.ts";
import { PRODUCT_STATUSES } from "../../../shared/mutations.ts";
import { editProducts, editVariants, type ProductPatch, type VariantPatch } from "../lib/catalog.ts";
import { ago, money } from "../lib/format.ts";
import { mutate } from "../lib/mutate.ts";
import { useRows } from "../lib/sync.ts";
import { Badge, Button, cx, Input, Select } from "../ui/controls.tsx";
import { DialogLayer } from "../ui/DialogLayer.tsx";
import { toast } from "../ui/toast.tsx";

export const STATUS_OPTIONS = PRODUCT_STATUSES.map((s) => ({ value: s, label: s[0] + s.slice(1).toLowerCase() }));

/** "$4.99", "4.99", "4,999.00" or a sum ("$24/6") → 4.99; "" → null; anything else → undefined (and a toast, unless quiet). */
export function parseMoney(text: string, label: string, { required = false, quiet = false } = {}): number | null | undefined {
  if (text.trim() === "") {
    if (required && !quiet) toast.error(`${label} can't be empty`);
    return required ? undefined : null;
  }
  const n = calc(text);
  if (n == null || n < 0) {
    if (!quiet) toast.error(`${label}: enter an amount like 19.99`);
    return undefined;
  }
  return Math.round(n * 10000) / 10000;
}

export const saveVariant = (item: Item, patch: VariantPatch) => editVariants([{ item_id: item.id, patch }]);
export const saveProduct = (productId: string | null, patch: ProductPatch) => (productId ? editProducts([{ product_id: productId, patch }]) : undefined);

export type WriteState = "pending" | "failed" | "reverted" | undefined;
/** Failed beats changed back beats pending beats nothing. */
export const worst = (...states: WriteState[]): WriteState =>
  states.includes("failed") ? "failed" : states.includes("reverted") ? "reverted" : states.includes("pending") ? "pending" : undefined;

const newestFirst = (a: { created_at: string }, b: { created_at: string }) => b.created_at.localeCompare(a.created_at);

/** Where edits stand with Shopify: on their way, refused, or changed back. Variant edits by item id, product edits by product id. */
export function useShopifyWrites() {
  const writes = useRows("shopify_write");
  return useMemo(() => {
    const item = new Map<string, WriteState>();
    const product = new Map<string, WriteState>();
    for (const w of writes) {
      if (w.status === "done") continue;
      const [map, key] = w.target === "variant" ? [item, w.item_id] : [product, w.shopify_product_id];
      if (key) map.set(key, worst(map.get(key), w.status));
    }
    return {
      pending: writes.filter((w) => w.status === "pending"),
      // Refused by Shopify, or accepted and then put back by something else in Shopify.
      problems: writes.filter((w) => w.status === "failed" || w.status === "reverted").sort(newestFirst),
      item,
      product,
    };
  }, [writes]);
}

const MARKERS = {
  pending: ["animate-pulse bg-warn", "Saving to Shopify…"],
  failed: ["bg-danger", "Shopify refused this change"],
  reverted: ["bg-danger", "Changed back in Shopify: another app or automation sets this"],
} as const;

/** A dot by a row whose edits are still on their way (amber), were refused or were changed back in Shopify (red). */
export function WriteMarker({ state }: { state: WriteState }) {
  if (!state) return null;
  const [color, title] = MARKERS[state];
  return <span className={cx("inline-block size-1.5 shrink-0 rounded-full", color)} title={title} />;
}

export function SyncStatus() {
  const { pending, problems } = useShopifyWrites();
  const items = useRows("item");
  const products = useRows("shopify_product");
  const [open, setOpen] = useState(false);
  const label = (w: (typeof problems)[number]) =>
    w.item_id ? (items.find((i) => i.id === w.item_id)?.name ?? "A variant") : (products.find((p) => p.id === w.shopify_product_id)?.title ?? "A product");
  const reverted = problems.filter((w) => w.status === "reverted").length;
  const failed = problems.length - reverted;

  if (!pending.length && !problems.length) return null;
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cx("flex h-7 items-center gap-1.5 rounded-md px-2 text-[12px]", problems.length ? "bg-danger-soft text-danger" : "bg-warn-soft text-warn")}
      >
        {problems.length ? <CircleAlert className="size-3.5" /> : <LoaderCircle className="size-3.5 animate-spin" />}
        {problems.length
          ? [failed && `${failed} failed`, reverted && `${reverted} changed back in Shopify`].filter(Boolean).join(" · ")
          : `Saving ${pending.length} to Shopify…`}
      </button>
      {open && problems.length > 0 && (
        <div className="absolute top-full right-0 z-30 mt-1 max-h-[70vh] w-[440px] [[data-stacked]_&]:right-auto [[data-stacked]_&]:left-0 overflow-y-auto rounded-lg border border-line bg-surface p-2 shadow-pop">
          <div className="px-2 pb-2 text-[12px] text-muted">
            HHL Ops shows what Shopify has now. Refused: fix and retry, or dismiss. Changed back: Shopify accepted the edit, then another app or automation in
            Shopify put the old value back, so change it in that app.
          </div>
          {problems.map((w) => (
            <div key={w.id} className="flex items-start gap-2 rounded-md px-2 py-1.5 hover:bg-hover">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="truncate font-medium">{label(w)}</span>
                  <Badge tone={w.status === "failed" ? "danger" : "warn"}>{w.status === "failed" ? "Refused" : "Changed back"}</Badge>
                </div>
                <div className="truncate text-[12px] text-muted">
                  {Object.entries(w.patch as Record<string, unknown>)
                    .map(([k, v]) => `${k} → ${Array.isArray(v) ? v.join(", ") : String(v ?? "empty")}`)
                    .join(" · ")}
                </div>
                <div className="text-[12px] text-danger">{w.error}</div>
                <div className="text-[11px] text-faint">{ago(w.created_at)}</div>
              </div>
              {w.status === "failed" && (
                <Button size="sm" variant="ghost" icon={<RotateCcw className="size-3.5" />} onClick={() => mutate("shopify.retry_write", { id: w.id })}>
                  Retry
                </Button>
              )}
              <button
                type="button"
                className="rounded p-1 text-faint hover:bg-hover hover:text-text"
                title="Dismiss"
                onClick={() => mutate("shopify.dismiss_write", { id: w.id })}
              >
                <X className="size-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

type Action = "price" | "compare_at" | "cost" | "status" | "add_tag" | "remove_tag" | "replace_title" | "vendor" | "product_type";
const ACTIONS: { value: Action; label: string; level: "variant" | "product" }[] = [
  { value: "price", label: "Price", level: "variant" },
  { value: "compare_at", label: "Compare-at price", level: "variant" },
  { value: "cost", label: "Cost", level: "variant" },
  { value: "status", label: "Status", level: "product" },
  { value: "add_tag", label: "Add Shopify tags", level: "product" },
  { value: "remove_tag", label: "Remove Shopify tags", level: "product" },
  { value: "replace_title", label: "Find and replace in titles", level: "product" },
  { value: "vendor", label: "Vendor (Shopify)", level: "product" },
  { value: "product_type", label: "Product type", level: "product" },
];

/** Shown while variants are selected in edit mode. */
export function BulkBar({ selected, onClear }: { selected: Item[]; onClear: () => void }) {
  const [action, setAction] = useState<Action | null>(null);
  const productIds = [...new Set(selected.map((i) => i.shopify_product_id).filter(Boolean))] as string[];
  return (
    <div className="flex h-11 shrink-0 items-center gap-2 border-b border-line bg-accent-soft/60 px-4">
      <span className="font-medium">
        {selected.length} variant{selected.length === 1 ? "" : "s"} selected
      </span>
      <span className="text-muted">
        in {productIds.length} product{productIds.length === 1 ? "" : "s"}
      </span>
      <div className="ml-3 flex flex-wrap gap-1">
        {ACTIONS.map((a) => (
          <Button key={a.value} size="sm" onClick={() => setAction(a.value)}>
            {a.label}
          </Button>
        ))}
      </div>
      <Button size="sm" variant="ghost" className="ml-auto" onClick={onClear}>
        Clear
      </Button>
      {action && <BulkDialog action={action} items={selected} productIds={productIds} onClose={() => setAction(null)} />}
    </div>
  );
}

function BulkDialog({ action, items, productIds, onClose }: { action: Action; items: Item[]; productIds: string[]; onClose: () => void }) {
  const products = useRows("shopify_product");
  const [mode, setMode] = useState<"set" | "pct" | "amount">("set");
  const [value, setValue] = useState(action === "status" ? "ACTIVE" : "");
  const [find, setFind] = useState("");
  const [busy, setBusy] = useState(false);
  const def = ACTIONS.find((a) => a.value === action);
  const byId = new Map(products.map((p) => [p.id, p]));
  const targets = productIds.map((id) => byId.get(id)).filter((p): p is ShopifyProduct => !!p);

  const field = action === "price" ? "price" : action === "compare_at" ? "compare_at_price" : "std_cost";
  const priceOf = (current: number | null, quiet = false): number | null | undefined => {
    if (mode === "set") return parseMoney(value, def?.label ?? "Amount", { required: action === "price", quiet });
    const n = numberOf(value);
    if (!Number.isFinite(n) || current == null) return current;
    const next = mode === "pct" ? current * (1 + n / 100) : current + n;
    return Math.max(0, Math.round(next * 100) / 100);
  };

  const preview = (() => {
    const first = items[0];
    if (def?.level === "variant" && first && value.trim()) {
      const after = priceOf(first[field], true);
      return after === undefined ? "" : `e.g. ${first.sku}: ${money(first[field]) || "empty"} → ${money(after) || "empty"}`;
    }
    if (action === "replace_title" && targets[0] && find) return `e.g. "${targets[0].title}" → "${targets[0].title.split(find).join(value)}"`;
    return "";
  })();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    let result: { ok: boolean } | undefined;
    if (def?.level === "variant") {
      if (mode !== "set" && (value.trim() === "" || !Number.isFinite(numberOf(value)))) {
        setBusy(false);
        return toast.error(mode === "pct" ? "Enter a percentage like 10 or -15" : "Enter an amount like 2 or -1.50");
      }
      const edits: { item_id: string; patch: VariantPatch }[] = [];
      for (const i of items) {
        const next = priceOf(i[field]);
        if (next === undefined) return setBusy(false);
        edits.push({ item_id: i.id, patch: { [field]: next } as VariantPatch });
      }
      result = await editVariants(edits);
    } else {
      const words = value
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);
      const edits = targets.flatMap((p): { product_id: string; patch: ProductPatch }[] => {
        if (action === "status") return [{ product_id: p.id, patch: { status: value as ProductPatch["status"] } }];
        if (action === "add_tag") return [{ product_id: p.id, patch: { tags: [...new Set([...p.tags, ...words])] } }];
        if (action === "remove_tag")
          return [{ product_id: p.id, patch: { tags: p.tags.filter((t) => !words.some((w) => w.toLowerCase() === t.toLowerCase())) } }];
        if (action === "replace_title")
          return find && p.title.includes(find) ? [{ product_id: p.id, patch: { title: p.title.split(find).join(value).trim() } }] : [];
        if (action === "vendor") return [{ product_id: p.id, patch: { vendor: value.trim() } }];
        return [{ product_id: p.id, patch: { product_type: value.trim() } }];
      });
      if (!edits.length) {
        setBusy(false);
        return toast.error(action === "replace_title" ? `No selected titles contain "${find}"` : "Nothing to change");
      }
      result = await editProducts(edits);
    }
    setBusy(false);
    if (result?.ok) {
      toast.ok("Saved; sending to Shopify");
      onClose();
    }
  };

  const money_ = def?.level === "variant";
  return (
    <DialogLayer onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form
        onSubmit={submit}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.stopPropagation();
            onClose();
          }
        }}
        className="w-[460px] rounded-xl border border-line bg-surface p-4 shadow-pop"
      >
        <div className="flex items-center gap-2 text-[15px] font-semibold">
          <Pencil className="size-4 text-muted" /> {def?.label}
        </div>
        <div className="mt-1 text-muted">
          {money_ ? `${items.length} variant${items.length === 1 ? "" : "s"}` : `${targets.length} product${targets.length === 1 ? "" : "s"}`} · saved in
          Shopify too
        </div>
        <div className="mt-4 flex flex-col gap-3">
          {money_ && (
            <Select
              value={mode}
              onChange={(e) => setMode(e.target.value as typeof mode)}
              options={[
                { value: "set", label: "Set to" },
                { value: "pct", label: "Change by %" },
                { value: "amount", label: "Change by $" },
              ]}
            />
          )}
          {action === "status" ? (
            <Select autoFocus value={value} onChange={(e) => setValue(e.target.value)} options={STATUS_OPTIONS} />
          ) : action === "replace_title" ? (
            <>
              <Input autoFocus value={find} placeholder="Find" onChange={(e) => setFind(e.target.value)} />
              <Input value={value} placeholder="Replace with (leave empty to remove)" onChange={(e) => setValue(e.target.value)} />
            </>
          ) : (
            <Input
              autoFocus
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={
                money_
                  ? mode === "set"
                    ? action === "price"
                      ? "e.g. 24.99"
                      : "e.g. 24.99 (empty clears it)"
                    : mode === "pct"
                      ? "e.g. 10 or -15"
                      : "e.g. 2 or -1.50"
                  : action.endsWith("tag")
                    ? "Tags, separated by commas"
                    : ""
              }
            />
          )}
          {preview && <div className="text-[12px] text-muted">{preview}</div>}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={busy} icon={<CircleCheck className="size-4" />}>
            Apply
          </Button>
        </div>
      </form>
    </DialogLayer>
  );
}
